export interface MealRow {
  meal_time?: string | null;
  created_at?: string | null;
  calories?: number | null;
  protein_g?: number | null;
  carbs_g?: number | null;
  fat_g?: number | null;
  telegram_user_id?: string;
}

export interface DietGoals {
  target_calories: number;
  protein_target: number;
  carbs_target: number;
  fat_target: number;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Local calendar date YYYY-MM-DD (avoids UTC midnight shifting the day). */
export function toLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getMealDate(meal: MealRow): Date | null {
  const raw = meal.meal_time ?? meal.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1971) return null;
  return d;
}

export function getUniqueMealDates(meals: MealRow[]): Set<string> {
  const dates = new Set<string>();
  for (const meal of meals) {
    const d = getMealDate(meal);
    if (d) dates.add(toLocalDateKey(d));
  }
  return dates;
}

export function getLastMealDate(meals: MealRow[]): string | null {
  let latest: Date | null = null;
  for (const meal of meals) {
    const d = getMealDate(meal);
    if (d && (!latest || d > latest)) latest = d;
  }
  return latest ? latest.toISOString() : null;
}

/**
 * Consecutive days with at least one meal logged.
 * If the client hasn't logged today yet, the streak still counts through yesterday.
 */
export function calculateLoggingStreak(meals: MealRow[]): number {
  const dates = getUniqueMealDates(meals);
  if (dates.size === 0) return 0;

  const todayKey = toLocalDateKey(new Date());
  const yesterdayKey = toLocalDateKey(addDays(new Date(), -1));

  let cursor = new Date();
  if (!dates.has(todayKey)) {
    if (!dates.has(yesterdayKey)) {
      return 0;
    }
    cursor = addDays(new Date(), -1);
  }

  let streak = 0;
  while (dates.has(toLocalDateKey(cursor))) {
    streak++;
    cursor = addDays(cursor, -1);
  }

  return streak;
}

export function calculateSuccessRate(meals: MealRow[], goals?: DietGoals | null): number {
  if (!goals || meals.length === 0) return 0;

  const thirtyDaysAgo = addDays(new Date(), -30);

  const recentMeals = meals.filter((m) => {
    const d = getMealDate(m);
    return d && d >= thirtyDaysAgo;
  });

  if (recentMeals.length === 0) return 0;

  const mealsByDate = new Map<string, MealRow[]>();
  recentMeals.forEach((meal) => {
    const d = getMealDate(meal);
    if (!d) return;
    const dateKey = toLocalDateKey(d);
    if (!mealsByDate.has(dateKey)) {
      mealsByDate.set(dateKey, []);
    }
    mealsByDate.get(dateKey)!.push(meal);
  });

  let totalSuccess = 0;
  let daysWithMeals = 0;

  mealsByDate.forEach((dayMeals) => {
    const dayCalories = dayMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
    const dayProtein = dayMeals.reduce((sum, meal) => sum + (meal.protein_g || 0), 0);
    const dayCarbs = dayMeals.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0);
    const dayFat = dayMeals.reduce((sum, meal) => sum + (meal.fat_g || 0), 0);

    const caloriesSuccess =
      dayCalories > 0 ? Math.min(100, (dayCalories / goals.target_calories) * 100) : 0;
    const proteinSuccess =
      dayProtein > 0 ? Math.min(100, (dayProtein / goals.protein_target) * 100) : 0;
    const carbsSuccess =
      dayCarbs > 0 ? Math.min(100, (dayCarbs / goals.carbs_target) * 100) : 0;
    const fatSuccess = dayFat > 0 ? Math.min(100, (dayFat / goals.fat_target) * 100) : 0;

    const daySuccess = (caloriesSuccess + proteinSuccess + carbsSuccess + fatSuccess) / 4;
    totalSuccess += daySuccess;
    daysWithMeals++;
  });

  return daysWithMeals > 0 ? Math.round(totalSuccess / daysWithMeals) : 0;
}

export function calculateDaysSinceLastMeal(meals: MealRow[]): number {
  if (meals.length === 0) return 999;

  let latest: Date | null = null;
  for (const meal of meals) {
    const d = getMealDate(meal);
    if (d && (!latest || d > latest)) latest = d;
  }

  if (!latest) return 999;

  const today = new Date();
  const diffTime = today.getTime() - latest.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}
